import React, { memo, useMemo } from 'react';
import type { BalanceSheetProps, BalanceSheetSection, BalanceSheetItem } from './BalanceSheet.types';

// Format currency value
const formatCurrency = (value: number, currency: string): string => {
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absValue);

  if (value < 0) {
    return `(${currency}${formatted})`;
  }
  return `${currency}${formatted}`;
};

// Calculate change percentage
const calculateChange = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : current < 0 ? -100 : 0;
  return ((current - previous) / Math.abs(previous)) * 100;
};

// Arrow icons
const TrendUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 8l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrendDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Single row component
interface RowProps {
  item: BalanceSheetItem;
  currency: string;
  showComparison: boolean;
  showChangePercent: boolean;
  compact: boolean;
  isIncome: boolean;
}

const Row = memo(({ item, currency, showComparison, showChangePercent, compact, isIncome }: RowProps) => {
  const isSubtotal = item.type === 'subtotal';
  const isTotal = item.type === 'total';
  const isHighlight = item.type === 'highlight';
  const indent = item.indent || 0;

  const change = item.previousValue !== undefined
    ? calculateChange(item.value, item.previousValue)
    : null;

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: showComparison
      ? showChangePercent ? '1fr 140px 140px 100px' : '1fr 140px 140px'
      : '1fr 160px',
    alignItems: 'center',
    padding: compact
      ? 'var(--space-2) var(--space-4)'
      : 'var(--space-3) var(--space-4)',
    borderBottom: isTotal ? 'none' : '1px solid var(--color-border-subtle)',
    background: isTotal
      ? 'var(--color-primary-50)'
      : isSubtotal
        ? 'var(--color-bg-secondary)'
        : isHighlight
          ? 'var(--color-warning-50)'
          : 'transparent',
    fontWeight: (isSubtotal || isTotal) ? 600 : 400,
    borderTop: isTotal ? '2px solid var(--color-primary-300)' : 'none'
  };

  const labelStyle: React.CSSProperties = {
    paddingLeft: `calc(${indent} * var(--space-4))`,
    fontSize: isTotal ? 'var(--text-sm)' : 'var(--text-sm)',
    color: isTotal
      ? 'var(--color-primary-700)'
      : isSubtotal
        ? 'var(--color-text-primary)'
        : 'var(--color-text-secondary)'
  };

  const valueStyle: React.CSSProperties = {
    textAlign: 'right',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--text-sm)',
    color: item.value < 0
      ? 'var(--color-negative)'
      : isTotal
        ? 'var(--color-primary-700)'
        : 'var(--color-text-primary)'
  };

  const changeColor = change !== null
    ? change > 0
      ? (isIncome ? 'var(--color-positive)' : 'var(--color-negative)')
      : change < 0
        ? (isIncome ? 'var(--color-negative)' : 'var(--color-positive)')
        : 'var(--color-text-tertiary)'
    : 'var(--color-text-tertiary)';

  return (
    <div style={rowStyle}>
      <div style={labelStyle}>
        {item.label}
        {item.note && (
          <span style={{
            marginLeft: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-tertiary)'
          }}>
            ({item.note})
          </span>
        )}
      </div>
      <div style={valueStyle}>
        {formatCurrency(item.value, currency)}
      </div>
      {showComparison && item.previousValue !== undefined && (
        <div style={{ ...valueStyle, color: 'var(--color-text-tertiary)' }}>
          {formatCurrency(item.previousValue, currency)}
        </div>
      )}
      {showComparison && showChangePercent && change !== null && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 'var(--space-1)',
          fontSize: 'var(--text-xs)',
          fontFamily: 'var(--font-mono)',
          color: changeColor
        }}>
          {change !== 0 && (change > 0 ? <TrendUpIcon /> : <TrendDownIcon />)}
          {change > 0 ? '+' : ''}{change.toFixed(1)}%
        </div>
      )}
    </div>
  );
});

Row.displayName = 'BalanceSheetRow';

// Section component
interface SectionProps {
  section: BalanceSheetSection;
  currency: string;
  showComparison: boolean;
  showChangePercent: boolean;
  compact: boolean;
}

const Section = memo(({ section, currency, showComparison, showChangePercent, compact }: SectionProps) => {
  const subtotal = useMemo(() => {
    return section.items.reduce((sum, item) => {
      if (item.type !== 'subtotal' && item.type !== 'total') {
        return sum + item.value;
      }
      return sum;
    }, 0);
  }, [section.items]);

  const previousSubtotal = useMemo(() => {
    return section.items.reduce((sum, item) => {
      if (item.type !== 'subtotal' && item.type !== 'total' && item.previousValue !== undefined) {
        return sum + item.previousValue;
      }
      return sum;
    }, 0);
  }, [section.items]);

  const sectionColor = section.type === 'income'
    ? 'var(--color-positive)'
    : section.type === 'expense'
      ? 'var(--color-negative)'
      : 'var(--color-primary)';

  const isIncome = section.type === 'income';

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: compact ? 'var(--space-2) var(--space-4)' : 'var(--space-3) var(--space-4)',
        background: 'var(--color-bg-tertiary)',
        borderLeft: `3px solid ${sectionColor}`,
        borderRadius: '0 var(--radius-sm) var(--radius-sm) 0'
      }}>
        <span style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-wide)',
          color: sectionColor
        }}>
          {section.title}
        </span>
      </div>

      {/* Section Items */}
      <div style={{
        background: 'var(--color-bg-card)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border-subtle)',
        overflow: 'hidden',
        marginTop: 'var(--space-2)'
      }}>
        {section.items.map(item => (
          <Row
            key={item.id}
            item={item}
            currency={currency}
            showComparison={showComparison}
            showChangePercent={showChangePercent}
            compact={compact}
            isIncome={isIncome}
          />
        ))}

        {/* Subtotal */}
        {section.showSubtotal && (
          <Row
            item={{
              id: `${section.title}-subtotal`,
              label: `Toplam ${section.title}`,
              value: subtotal,
              previousValue: previousSubtotal > 0 ? previousSubtotal : undefined,
              type: 'subtotal'
            }}
            currency={currency}
            showComparison={showComparison && previousSubtotal > 0}
            showChangePercent={showChangePercent}
            compact={compact}
            isIncome={isIncome}
          />
        )}
      </div>
    </div>
  );
});

Section.displayName = 'BalanceSheetSection';

// Main component
function BalanceSheetComponent({
  title = 'Bilanço Özeti',
  subtitle,
  sections,
  currency = '₺',
  showComparison = false,
  currentPeriodLabel = 'Bu Dönem',
  previousPeriodLabel = 'Önceki Dönem',
  showChangePercent = true,
  className,
  compact = false
}: BalanceSheetProps): React.ReactElement {

  // Calculate totals
  const totals = useMemo(() => {
    let totalIncome = 0;
    let totalExpense = 0;
    let prevTotalIncome = 0;
    let prevTotalExpense = 0;

    sections.forEach(section => {
      section.items.forEach(item => {
        if (item.type !== 'subtotal' && item.type !== 'total') {
          if (section.type === 'income') {
            totalIncome += item.value;
            if (item.previousValue) prevTotalIncome += item.previousValue;
          } else if (section.type === 'expense') {
            totalExpense += item.value;
            if (item.previousValue) prevTotalExpense += item.previousValue;
          }
        }
      });
    });

    return {
      income: totalIncome,
      expense: totalExpense,
      net: totalIncome - totalExpense,
      prevIncome: prevTotalIncome,
      prevExpense: prevTotalExpense,
      prevNet: prevTotalIncome - prevTotalExpense
    };
  }, [sections]);

  return (
    <div
      className={className}
      style={{
        background: 'var(--color-bg-card)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border-default)',
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{
        padding: compact ? 'var(--space-4)' : 'var(--space-5) var(--space-6)',
        borderBottom: '1px solid var(--color-border-default)',
        background: 'var(--color-bg-secondary)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--text-lg)',
              fontWeight: 700,
              fontFamily: 'var(--font-display)',
              color: 'var(--color-text-primary)'
            }}>
              {title}
            </h2>
            {subtitle && (
              <p style={{
                margin: 'var(--space-1) 0 0',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-secondary)'
              }}>
                {subtitle}
              </p>
            )}
          </div>

          {/* Net Result Badge */}
          <div style={{
            padding: 'var(--space-2) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: totals.net >= 0 ? 'var(--color-positive-bg)' : 'var(--color-negative-bg)',
            border: `1px solid ${totals.net >= 0 ? 'var(--color-positive-border)' : 'var(--color-negative-border)'}`
          }}>
            <div style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 500,
              color: totals.net >= 0 ? 'var(--color-positive)' : 'var(--color-negative)',
              marginBottom: '2px'
            }}>
              Net {totals.net >= 0 ? 'Kâr' : 'Zarar'}
            </div>
            <div style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              color: totals.net >= 0 ? 'var(--color-positive)' : 'var(--color-negative)'
            }}>
              {formatCurrency(totals.net, currency)}
            </div>
          </div>
        </div>

        {/* Column Headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: showComparison
            ? showChangePercent ? '1fr 140px 140px 100px' : '1fr 140px 140px'
            : '1fr 160px',
          marginTop: 'var(--space-4)',
          padding: 'var(--space-2) var(--space-4)',
          background: 'var(--color-bg-tertiary)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-wide)',
          color: 'var(--color-text-tertiary)'
        }}>
          <div>Kalem</div>
          <div style={{ textAlign: 'right' }}>{currentPeriodLabel}</div>
          {showComparison && (
            <div style={{ textAlign: 'right' }}>{previousPeriodLabel}</div>
          )}
          {showComparison && showChangePercent && (
            <div style={{ textAlign: 'right' }}>Değişim</div>
          )}
        </div>
      </div>

      {/* Sections */}
      <div style={{ padding: compact ? 'var(--space-3)' : 'var(--space-4) var(--space-6)' }}>
        {sections.map((section, index) => (
          <Section
            key={index}
            section={section}
            currency={currency}
            showComparison={showComparison}
            showChangePercent={showChangePercent}
            compact={compact}
          />
        ))}

        {/* Grand Total */}
        <div style={{
          marginTop: 'var(--space-4)',
          padding: 'var(--space-4)',
          background: totals.net >= 0
            ? 'linear-gradient(135deg, var(--color-positive-bg), var(--color-bg-card))'
            : 'linear-gradient(135deg, var(--color-negative-bg), var(--color-bg-card))',
          borderRadius: 'var(--radius-lg)',
          border: `2px solid ${totals.net >= 0 ? 'var(--color-positive-border)' : 'var(--color-negative-border)'}`,
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: showComparison
              ? showChangePercent ? '1fr 140px 140px 100px' : '1fr 140px 140px'
              : '1fr 160px',
            alignItems: 'center'
          }}>
            <div style={{
              fontSize: 'var(--text-base)',
              fontWeight: 700,
              color: totals.net >= 0 ? 'var(--color-positive)' : 'var(--color-negative)'
            }}>
              NET SONUÇ
            </div>
            <div style={{
              textAlign: 'right',
              fontSize: 'var(--text-xl)',
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              color: totals.net >= 0 ? 'var(--color-positive)' : 'var(--color-negative)'
            }}>
              {formatCurrency(totals.net, currency)}
            </div>
            {showComparison && (
              <div style={{
                textAlign: 'right',
                fontSize: 'var(--text-base)',
                fontFamily: 'var(--font-mono)',
                color: 'var(--color-text-tertiary)'
              }}>
                {formatCurrency(totals.prevNet, currency)}
              </div>
            )}
            {showComparison && showChangePercent && totals.prevNet !== 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 600,
                color: calculateChange(totals.net, totals.prevNet) >= 0
                  ? 'var(--color-positive)'
                  : 'var(--color-negative)'
              }}>
                {calculateChange(totals.net, totals.prevNet) >= 0 ? <TrendUpIcon /> : <TrendDownIcon />}
                {calculateChange(totals.net, totals.prevNet) > 0 ? '+' : ''}
                {calculateChange(totals.net, totals.prevNet).toFixed(1)}%
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const BalanceSheet = memo(BalanceSheetComponent);
